/** 用户最后活跃时间（多标签共享） */
export const IDLE_STORAGE_KEY = 'lastActiveAt'

/** 无操作超时：120 分钟 */
export const IDLE_TIMEOUT_MS = 120 * 60 * 1000

const CHECK_INTERVAL_MS = 30_000
const THROTTLE_MS = 5_000

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'wheel'
] as const

let timer: ReturnType<typeof setInterval> | null = null
let lastWrite = 0
let loggingOut = false
let storageListener: ((ev: StorageEvent) => void) | null = null
let started = false

/** 登录页、SSO 回调（URL 带 code）不跑空闲超时 */
export const isIdleExemptRoute = (to?: {
  path?: string
  query?: Record<string, unknown>
}): boolean => {
  const path = to?.path ?? window.location.pathname
  if (path === '/login' || path.startsWith('/login')) {
    return true
  }
  if (to?.query && to.query.code != null && String(to.query.code) !== '') {
    return true
  }
  try {
    if (new URLSearchParams(window.location.search).has('code')) {
      return true
    }
  } catch {
    // ignore
  }
  return false
}

/** 记录一次用户操作（节流）；force 时立即写入（登录成功等） */
export const touchActivity = (force = false) => {
  const now = Date.now()
  if (!force && now - lastWrite < THROTTLE_MS) {
    return
  }
  lastWrite = now
  localStorage.setItem(IDLE_STORAGE_KEY, String(now))
}

const onUserActivity = () => {
  touchActivity()
}

/** 是否已超过空闲时限 */
export const isIdleExpired = (): boolean => {
  const raw = localStorage.getItem(IDLE_STORAGE_KEY)
  if (!raw) {
    return false
  }
  const ts = Number(raw)
  if (!Number.isFinite(ts)) {
    return false
  }
  return Date.now() - ts > IDLE_TIMEOUT_MS
}

/** 清除空闲标记（退出登录时调用） */
export const clearIdleMark = () => {
  localStorage.removeItem(IDLE_STORAGE_KEY)
  lastWrite = 0
  loggingOut = false
}

const checkAndLogout = () => {
  if (loggingOut || isIdleExemptRoute()) {
    return
  }
  if (!localStorage.getItem('accessToken') && !localStorage.getItem('refreshToken')) {
    return
  }
  if (!isIdleExpired()) {
    return
  }
  loggingOut = true
  // 动态导入避免与 authFailure 循环依赖
  void import('@/utils/authFailure').then(({ handleAuthFailure }) => {
    handleAuthFailure({ reason: 'idle' })
  })
}

/** 启动全局空闲监听（幂等） */
export const startIdleWatch = () => {
  if (started) {
    return
  }
  started = true
  // 已登录且无时间戳时从现在起算，避免刷新后误踢
  if (
    (localStorage.getItem('accessToken') || localStorage.getItem('refreshToken')) &&
    !localStorage.getItem(IDLE_STORAGE_KEY)
  ) {
    touchActivity(true)
  }
  ACTIVITY_EVENTS.forEach((e) => {
    window.addEventListener(e, onUserActivity, { passive: true })
  })
  storageListener = (ev: StorageEvent) => {
    if (ev.key === IDLE_STORAGE_KEY && ev.newValue) {
      lastWrite = Date.now()
    }
  }
  window.addEventListener('storage', storageListener)
  timer = setInterval(checkAndLogout, CHECK_INTERVAL_MS)
}

/** 停止空闲监听 */
export const stopIdleWatch = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  if (started) {
    ACTIVITY_EVENTS.forEach((e) => {
      window.removeEventListener(e, onUserActivity)
    })
  }
  if (storageListener) {
    window.removeEventListener('storage', storageListener)
    storageListener = null
  }
  started = false
}
